/**
 * model.js — Bet record data model for the Bettor Advantage bet-tracking hub.
 *
 * Framework-free, plain ES2020 JavaScript: no imports, no I/O, no Node or
 * browser APIs beyond the language itself. This file (and stats.js) are
 * designed to be copied verbatim into the React website and the React
 * Native app later.
 *
 * A "bet" is a single wager as reported by a sportsbook (via the sync
 * adapter), entered manually by the user, or imported from CSV. The hub is
 * READ-ONLY with respect to sportsbooks: bets are only ever pulled in,
 * never placed.
 */

/** Supported bet types. */
export const BET_TYPES = Object.freeze([
  'moneyline',
  'spread',
  'total',
  'prop',
  'parlay',
  'future',
]);

/**
 * Bet lifecycle statuses.
 * - draft:      parsed from user input (ingest/) but NOT yet confirmed
 *               by the user. Drafts are not real bets: the stats engine
 *               ignores them entirely (not settled, not pending, no
 *               exposure). A draft becomes a real bet only when the
 *               user confirms it, at which point its status is set to
 *               'pending' (or a settled status for a past bet).
 * - pending:    placed, not yet settled (open exposure)
 * - won:        settled as a win
 * - lost:       settled as a loss
 * - push:       settled as a push / void (stake returned)
 * - cashed_out: user cashed out early for cashOutAmount (see below)
 */
export const BET_STATUSES = Object.freeze([
  'draft',
  'pending',
  'won',
  'lost',
  'push',
  'cashed_out',
]);

/**
 * @typedef {Object} BetLeg
 * @property {string} event      Event description for this leg.
 * @property {string} selection  Selection description for this leg.
 * @property {string} betType    One of BET_TYPES (never 'parlay' for a leg).
 * @property {number} [line]     Spread/total line for this leg, if any.
 * @property {number} [margin]   Signed decision margin for this leg
 *                               (see decisionMargin below).
 */

/**
 * @typedef {Object} Bet
 * @property {string} id                 Unique id (provider id, or local).
 * @property {{key: string, name: string}} book  Sportsbook.
 * @property {string} sport              e.g. 'NFL', 'NBA', 'MLB'.
 * @property {string} league             League label (often same as sport).
 * @property {string} event              e.g. 'Kansas City Chiefs at Buffalo Bills'.
 * @property {string} selection          e.g. 'Kansas City Chiefs -3.5'.
 * @property {string} betType            One of BET_TYPES.
 * @property {number} oddsAmerican       American odds, e.g. -110 or +150.
 * @property {number} stake              Amount risked, in dollars.
 * @property {string} placedAt           ISO 8601 timestamp the bet was placed.
 * @property {string} commenceTime       ISO 8601 start time of the event.
 * @property {string|null} settledAt     ISO 8601 settlement time, if settled.
 * @property {string} status             One of BET_STATUSES.
 * @property {number|null} [line]        Spread or total line, if applicable.
 * @property {number|null} [cashOutAmount]  For cashed_out bets: the amount
 *                                       the book paid out (stake + profit
 *                                       or stake - loss). Required to value
 *                                       a cashed_out bet.
 * @property {number|null} [margin]      Signed decision margin (see below).
 * @property {BetLeg[]} [legs]           Parlay legs (betType === 'parlay').
 * @property {string} [source]           Where the record came from:
 *                                       'sync' | 'manual' | 'csv' (sync
 *                                       layer), or 'link' | 'text' |
 *                                       'image' (chat-box ingest, see
 *                                       ingest/). Ingest sources always
 *                                       start life as status 'draft'.
 * @property {string} [externalId]       Provider/sportsbook bet id, if any.
 *
 * DECISION MARGIN (`margin`) — the signed number that powers the
 * "closest win / closest loss" stats. It is always expressed from the
 * bettor's perspective, in the natural scoring unit of the bet:
 *   - spread bet: (final scoring margin for the backed side) - (line).
 *       Backed Chiefs -3.5 and they win by 4  -> margin = +0.5 (won by a hair)
 *       Backed Chiefs +3.5 and they lose by 4 -> margin = -0.5 (lost by a hair)
 *   - total bet: (final combined score) - (line) for Overs;
 *                (line) - (final combined score) for Unders.
 *       Over 47.5, final total 48 -> margin = +0.5. Under 47.5, final 51 -> -3.5.
 *   - moneyline: final scoring margin of the backed side
 *       (win by 1 -> +1; lose by 21 -> -21).
 *   - prop / future: the analogous margin in the prop's unit where the book
 *     reports one (e.g. player yards over/under); otherwise null.
 * Sign convention: margin > 0 => won, margin < 0 => lost, margin === 0 =>
 * push. A null margin simply excludes the bet from closest-win/loss stats
 * (it still counts everywhere else).
 *
 * PARLAY MARGIN RULE: a parlay's margin is derived from its legs when the
 * legs carry margins (see betMargin in this file):
 *   - won parlay:  the MINIMUM leg margin — the leg that came closest to
 *     losing is how close the parlay was.
 *   - lost parlay: the losing leg closest to covering, i.e. the MAXIMUM
 *     (least negative) margin among losing legs.
 * An explicit `margin` on the parlay itself always wins over the leg rule.
 */

/** Round to cents. All money leaving this module is rounded to 2dp. */
export function round2(n) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * Convert American odds to decimal odds.
 * +150 -> 2.50, -110 -> 1.9090..., 100/-100 -> 2.
 */
export function americanToDecimal(oddsAmerican) {
  if (typeof oddsAmerican !== 'number' || oddsAmerican === 0) return null;
  if (oddsAmerican > 0) return 1 + oddsAmerican / 100;
  return 1 + 100 / Math.abs(oddsAmerican);
}

/** True for statuses that count as settled (money outcome is final). */
export function isSettled(bet) {
  return bet.status === 'won' || bet.status === 'lost' ||
    bet.status === 'push' || bet.status === 'cashed_out';
}

/** True for statuses that decide win rate / record (won or lost only). */
export function isDecided(bet) {
  return bet.status === 'won' || bet.status === 'lost';
}

/**
 * Profit of a bet in dollars (2dp), or null if it is not settled.
 * Derived deterministically — never stored on the record — so every
 * source (sync, manual, CSV) is valued identically:
 *   won:        stake * (decimalOdds - 1)
 *   lost:       -stake
 *   push:       0
 *   cashed_out: cashOutAmount - stake (null if cashOutAmount unknown)
 *   pending:    null
 */
export function profitForBet(bet) {
  switch (bet.status) {
    case 'won': {
      const dec = americanToDecimal(bet.oddsAmerican);
      if (dec === null) return null;
      return round2(bet.stake * (dec - 1));
    }
    case 'lost':
      return round2(-bet.stake);
    case 'push':
      return 0;
    case 'cashed_out':
      return typeof bet.cashOutAmount === 'number'
        ? round2(bet.cashOutAmount - bet.stake)
        : null;
    default:
      return null;
  }
}

/**
 * Total payout (stake returned + profit) in dollars, or null if not
 * settled / not yet valueable. Lost bets pay out 0; pushes return stake.
 */
export function payoutForBet(bet) {
  const profit = profitForBet(bet);
  if (profit === null) return null;
  return round2(bet.stake + profit);
}

/**
 * The signed decision margin for a bet (see the typedef above), or null
 * when it cannot be determined. Explicit bet.margin wins; parlays fall
 * back to the leg rule; single bets without a recorded margin -> null.
 */
export function decisionMargin(bet) {
  if (typeof bet.margin === 'number') return bet.margin;
  if (bet.betType === 'parlay' && Array.isArray(bet.legs)) {
    const legMargins = bet.legs
      .map((leg) => (typeof leg.margin === 'number' ? leg.margin : null))
      .filter((m) => m !== null);
    if (legMargins.length === 0) return null;
    if (bet.status === 'won') return Math.min(...legMargins);
    if (bet.status === 'lost') {
      const losing = legMargins.filter((m) => m < 0);
      return losing.length ? Math.max(...losing) : Math.min(...legMargins);
    }
    return null;
  }
  return null;
}

let localIdCounter = 0;

/**
 * Create a bet record with defaults filled in. Throws nothing; use
 * validateBet() to check a record before persisting it.
 */
export function createBet(partial = {}) {
  localIdCounter += 1;
  return {
    id: partial.id ?? `local-${Date.now()}-${localIdCounter}`,
    book: partial.book ?? { key: 'unknown', name: 'Unknown book' },
    sport: partial.sport ?? '',
    league: partial.league ?? partial.sport ?? '',
    event: partial.event ?? '',
    selection: partial.selection ?? '',
    betType: partial.betType ?? 'moneyline',
    oddsAmerican: partial.oddsAmerican ?? -110,
    stake: partial.stake ?? 0,
    placedAt: partial.placedAt ?? new Date().toISOString(),
    commenceTime: partial.commenceTime ?? partial.placedAt ?? new Date().toISOString(),
    settledAt: partial.settledAt ?? null,
    status: partial.status ?? 'pending',
    line: partial.line ?? null,
    cashOutAmount: partial.cashOutAmount ?? null,
    margin: partial.margin ?? null,
    legs: partial.legs ?? undefined,
    source: partial.source ?? 'manual',
    externalId: partial.externalId ?? undefined,
  };
}

/**
 * Validate a bet record. Returns an array of human-readable problems
 * (empty array = valid). Deliberately lenient about optional fields.
 */
export function validateBet(bet) {
  const problems = [];
  if (!bet || typeof bet !== 'object') return ['bet is not an object'];
  if (!bet.id) problems.push('missing id');
  if (!bet.book || !bet.book.key) problems.push('missing book.key');
  if (!BET_TYPES.includes(bet.betType)) problems.push(`unknown betType: ${bet.betType}`);
  if (!BET_STATUSES.includes(bet.status)) problems.push(`unknown status: ${bet.status}`);
  if (typeof bet.oddsAmerican !== 'number' || bet.oddsAmerican === 0 ||
      (bet.oddsAmerican > -100 && bet.oddsAmerican < 100)) {
    problems.push(`invalid American odds: ${bet.oddsAmerican}`);
  }
  if (typeof bet.stake !== 'number' || bet.stake < 0) problems.push(`invalid stake: ${bet.stake}`);
  if (bet.status === 'cashed_out' && typeof bet.cashOutAmount !== 'number') {
    problems.push('cashed_out bet requires a numeric cashOutAmount');
  }
  if (Number.isNaN(Date.parse(bet.placedAt))) problems.push(`invalid placedAt: ${bet.placedAt}`);
  return problems;
}
